import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import LiveTrackingMap from "@/components/LiveTrackingMap";
import useRealtimeOrders from "@/hooks/useRealtimeOrders";

const KITCHEN_LABELS = { accepted: "Le restaurant a accepté votre commande", preparing: "Votre commande est en préparation", ready: "Votre commande est prête", rejected: "Le restaurant a refusé la commande" };
import TaxiLiveMeter from "@/components/orderTracking/TaxiLiveMeter";
import PendingPaymentBanner from "@/components/orderTracking/PendingPaymentBanner";
import CourierRatingCard from "@/components/orderTracking/CourierRatingCard";
import ShareOrderButton from "@/components/ShareOrderButton";
import { MapPin, Package, Truck, Clock, Phone, Star, Navigation, MessageSquare, AlertTriangle, CreditCard, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderTracking({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('id');
  const [trackingData, setTrackingData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showRating, setShowRating] = useState(false);
  const [ratingScore, setRatingScore] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [hasRated, setHasRated] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);
  // Dispute (signalement)
  const [showDispute, setShowDispute] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeComment, setDisputeComment] = useState("");
  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [existingDispute, setExistingDispute] = useState(null);
  const [disputeReasons, setDisputeReasons] = useState([]);
  // Pending payment instructions (mobile money flow) stored in sessionStorage par NewOrder
  const [pendingPayment, setPendingPayment] = useState(() => {
    if (!searchParams.get('id')) return null;
    try {
      const raw = sessionStorage.getItem(`payment_${searchParams.get('id')}`);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const loadDisputeReasons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/disputes/reasons`);
      setDisputeReasons(res.data);
    } catch (err) {
      console.warn("Failed to load dispute reasons:", err);
    }
  }, []);

  const checkExistingDispute = useCallback(async () => {
    if (!orderId) return;
    try {
      const res = await axios.get(`${API}/disputes/order/${orderId}`);
      if (res.data && res.data.length > 0) {
        setExistingDispute(res.data[0]);
      }
    } catch (err) {
      console.warn("Failed to check existing dispute:", err);
    }
  }, [orderId]);

  const checkRatingStatus = useCallback(async () => {
    if (!orderId || !user?.id) return;
    try {
      const response = await axios.get(`${API}/ratings/check/${orderId}/${user.id}`);
      setHasRated(response.data.has_rated);
    } catch (err) {
      console.warn("Failed to check rating status:", err);
    }
  }, [orderId, user?.id]);

  const fetchTrackingData = useCallback(async () => {
    if (!orderId) return;
    try {
      const response = await axios.get(`${API}/orders/${orderId}/tracking`);
      setTrackingData(response.data);
    } catch (error) {
      console.error("Failed to fetch tracking data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    fetchTrackingData();
    checkRatingStatus();
    checkExistingDispute();
    loadDisputeReasons();
    const interval = setInterval(fetchTrackingData, 2000);
    return () => clearInterval(interval);
  }, [orderId, fetchTrackingData, checkRatingStatus, checkExistingDispute, loadDisputeReasons]);

  // WebSocket temps réel — refresh instantané à l'assignation, changement de statut,
  // ET réception des pings GPS du livreur (mise à jour fluide du marqueur sur la carte).
  const [liveCourierPos, setLiveCourierPos] = useState(null);
  const [taxiMeter, setTaxiMeter] = useState(null);  // { distance_km, current_fare, current_location }
  useRealtimeOrders(user?.id, (event, data) => {
    if (event === "courier_assigned") {
      if (!orderId || data?.order_id !== orderId) return;
      toast.success(`Livreur ${data?.courier?.name || ""} assigné !`);
      fetchTrackingData();
    } else if (event === "order_status_changed" || event === "restaurant_status_changed") {
      if (!orderId || data?.order_id !== orderId) return;
      if (event === "restaurant_status_changed") toast.info(KITCHEN_LABELS[data?.restaurant_status] || "Mise à jour du restaurant");
      fetchTrackingData();
    } else if (event === "courier_location_update") {
      if (data?.order_ids?.includes(orderId) && data?.latitude && data?.longitude) {
        setLiveCourierPos([data.latitude, data.longitude]);
      }
    } else if (event === "taxi_fare_updated") {
      // Compteur taxi live : mise à jour fluide pendant la course
      if (data?.order_id === orderId) {
        setTaxiMeter({
          distance_km: data.distance_km,
          current_fare: data.current_fare,
          current_location: data.current_location,
        });
      }
    }
  });

  const submitDispute = async () => {
    if (!disputeReason) {
      toast.error("Veuillez sélectionner un motif");
      return;
    }
    setSubmittingDispute(true);
    try {
      const res = await axios.post(`${API}/disputes`, {
        order_id: orderId,
        customer_id: user.id,
        reason: disputeReason,
        comment: disputeComment || null,
      });
      toast.success("Votre signalement a été envoyé à notre équipe.");
      setExistingDispute(res.data.dispute);
      setShowDispute(false);
      setDisputeReason("");
      setDisputeComment("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du signalement");
    } finally {
      setSubmittingDispute(false);
    }
  };

  const submitRating = async () => {
    if (ratingScore < 1) {
      toast.error("Veuillez sélectionner une note");
      return;
    }
    setSubmittingRating(true);
    try {
      const ratedId = user.role === "client"
        ? trackingData.courier?.id
        : trackingData.customer_id;

      await axios.post(`${API}/ratings`, {
        order_id: orderId,
        rater_id: user.id,
        rated_id: ratedId,
        rater_role: user.role,
        score: ratingScore,
        comment: ratingComment || null
      });
      toast.success("Merci pour votre notation !");
      setHasRated(true);
      setShowRating(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSubmittingRating(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusConfig = {
      pending: { label: "En attente", icon: Clock, color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200" },
      preparing: { label: "En préparation", icon: Package, color: "text-[#003594]", bg: "bg-[#003594]/10 border-[#003594]/30" },
      ready: { label: "Prête", icon: Package, color: "text-cyan-600", bg: "bg-cyan-50 border-cyan-200" },
      assigned: { label: "Livreur en route", icon: Truck, color: "text-indigo-600", bg: "bg-indigo-50 border-indigo-200" },
      picked_up: { label: "Colis récupéré", icon: Package, color: "text-purple-600", bg: "bg-purple-50 border-purple-200" },
      in_transit: { label: "En livraison", icon: Navigation, color: "text-orange-600", bg: "bg-orange-50 border-orange-200" },
      delivered: { label: "Livrée ✓", icon: Package, color: "text-green-600", bg: "bg-green-50 border-green-200" },
      cancelled: { label: "Annulée", icon: Package, color: "text-red-600", bg: "bg-red-50 border-red-200" }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const getStatusTextColor = (status) => {
    if (status === 'delivered') return 'text-emerald-400';
    if (['assigned', 'picked_up', 'in_transit'].includes(status)) return 'text-[#D4AF37]';
    return 'text-blue-300';
  };

  const getStatusSteps = () => {
    const steps = [
      { key: 'pending', label: 'Commande reçue' },
      { key: 'preparing', label: 'En préparation' },
      { key: 'ready', label: 'Prête' },
      { key: 'assigned', label: 'Livreur assigné' },
      { key: 'in_transit', label: 'En route' },
      { key: 'delivered', label: 'Livrée' }
    ];

    const statusOrder = ['pending', 'preparing', 'ready', 'assigned', 'picked_up', 'in_transit', 'delivered'];
    // Commande restaurant : l'avancement cuisine (acceptée / en préparation / prête) fait progresser la frise
    const kitchen = { accepted: 'preparing', preparing: 'preparing', ready: 'ready' }[trackingData?.restaurant_status];
    const currentIndex = Math.max(statusOrder.indexOf(trackingData?.status), kitchen ? statusOrder.indexOf(kitchen) : -1);

    return steps.map((step, index) => ({
      ...step,
      completed: statusOrder.indexOf(step.key) <= currentIndex,
      current: step.key === trackingData?.status || (kitchen && step.key === kitchen && statusOrder.indexOf(trackingData?.status) < currentIndex) ||
               (step.key === 'in_transit' && trackingData?.status === 'picked_up')
    }));
  };

  if (!orderId) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-[#1E293B] border border-white/10 rounded-[2rem] text-white">
          <CardContent className="pt-6 text-center">
            <p className="text-white font-bold mb-2">Aucune commande sélectionnée</p>
            <p className="text-slate-400 text-xs mb-5">Lien de suivi incomplet — retournez à votre tableau de bord.</p>
            <Button onClick={() => navigate('/dashboard')} className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] rounded-full font-black uppercase tracking-widest text-xs" data-testid="tracking-fallback-back-btn">
              Retour au tableau de bord
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-white font-bold uppercase tracking-widest text-sm">Chargement du suivi…</p>
        </div>
      </div>
    );
  }

  if (!trackingData) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <Card className="max-w-md bg-[#1E293B] border border-white/10 rounded-[2rem] text-white">
          <CardContent className="pt-6">
            <p className="text-center text-white font-bold mb-4">Commande introuvable</p>
            <Button onClick={() => navigate('/dashboard')} className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] rounded-full font-black uppercase tracking-widest text-xs">
              Retour au tableau de bord
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const kitchenStatus = trackingData.status === 'pending' && { accepted: 'preparing', preparing: 'preparing', ready: 'ready' }[trackingData.restaurant_status];
  const statusInfo = getStatusInfo(kitchenStatus || trackingData.status);
  const StatusIcon = statusInfo.icon;
  const steps = getStatusSteps();
  const showLiveTracking = ['assigned', 'picked_up', 'in_transit'].includes(trackingData.status);

  // Compute ETA string (simple heuristic if not provided)
  const etaLabel = trackingData.eta || "~ 20 min";
  const courierInitials = (trackingData.courier?.name || "AR").split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-28">
      {/* Premium Header — "EXPÉDITION" */}
      <header className="p-6 pt-8 bg-[#1E293B] rounded-b-[3rem] shadow-2xl border-b border-white/5 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto flex justify-between items-center">
          <button
            onClick={() => navigate('/home')}
            className="bg-[#0F172A] border border-white/10 rounded-2xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-[#D4AF37] hover:border-[#D4AF37]/40"
            data-testid="tracking-back-btn"
          >
            ← Retour
          </button>
          <div className="text-center">
            <h1 className="text-xl md:text-2xl font-black uppercase tracking-tighter italic text-white" style={{fontFamily: 'Outfit, sans-serif'}}>
              EXPÉDITION
            </h1>
            <p className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.25em]">Standard ARTEKNOS Logistics</p>
          </div>
          <div className="bg-[#D4AF37]/10 p-3 rounded-2xl border border-[#D4AF37]/20">
            <Navigation size={20} className="text-[#D4AF37]" />
          </div>
        </div>
      </header>

      <div className="px-4 md:px-8 py-6">
        <div className="max-w-5xl mx-auto space-y-6">

          {/* COMPTEUR TAXI LIVE — visible uniquement pour les courses taxi en cours */}
          {trackingData?.order_type === "taxi" && (
            <TaxiLiveMeter taxiMeter={taxiMeter} />
          )}

          {/* Bannière paiement Wave / Orange / Free Money en attente */}
          {trackingData?.status !== 'delivered' && (
            <PendingPaymentBanner
              pendingPayment={pendingPayment}
              onDismiss={() => {
                sessionStorage.removeItem(`payment_${orderId}`);
                setPendingPayment(null);
              }}
            />
          )}

          {/* Premium Course Card — inspiré du mockup "Course #ART-XXX" */}
          <Card className="bg-[#1E293B] border border-white/5 rounded-[2.5rem] shadow-2xl relative overflow-hidden" data-testid="premium-course-card">
            <div className="absolute -top-20 -right-20 w-52 h-52 bg-[#003594]/25 rounded-full blur-3xl pointer-events-none" />
            <CardContent className="py-6 relative z-10">
              <div className="flex justify-between items-start mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#003594] flex items-center justify-center border border-white/10 shadow-lg">
                    <Navigation size={20} className="text-[#D4AF37] rotate-45" />
                  </div>
                  <div>
                    <h4 className="font-black text-sm uppercase tracking-tight text-white">Course #{trackingData.order_id?.substring(0, 6).toUpperCase()}</h4>
                    <p className={`text-[10px] font-bold uppercase tracking-widest ${getStatusTextColor(trackingData.status)}`}>
                      {statusInfo.label}
                    </p>
                  </div>
                </div>
                {showLiveTracking && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
                    <span className="animate-pulse w-2 h-2 bg-emerald-400 rounded-full"></span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37]">GPS LIVE</span>
                  </div>
                )}
              </div>

              {/* Progress Steps - luxury */}
              <div className="flex justify-between relative mt-4">
                <div className="absolute top-4 left-0 right-0 h-1 bg-white/10 -z-10"></div>
                <div
                  className="absolute top-4 left-0 h-1 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] -z-10 transition-all duration-500 rounded-full"
                  style={{ width: `${Math.max(0, (steps.filter(s => s.completed).length - 1) / (steps.length - 1) * 100)}%` }}
                ></div>

                {steps.map((step, index) => (
                  <div key={step.key} className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black border ${
                      step.completed
                        ? 'bg-gradient-to-br from-[#D4AF37] to-[#B8962E] text-[#0F172A] border-[#D4AF37]'
                        : 'bg-[#0F172A] text-gray-500 border-white/10'
                    } ${step.current ? 'ring-2 ring-[#D4AF37]/40 shadow-lg shadow-[#D4AF37]/20' : ''}`}>
                      {step.completed ? '✓' : index + 1}
                    </div>
                    <p className={`text-[9px] mt-2 text-center max-w-[70px] font-bold uppercase tracking-widest ${step.completed ? 'text-[#D4AF37]' : 'text-gray-500'}`}>
                      {step.label}
                    </p>
                  </div>
                ))}
              </div>

              {/* Driver Avatar Card */}
              {trackingData.courier && (
                <div className="flex items-center justify-between pt-6 mt-6 border-t border-white/10" data-testid="tracking-driver-card">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center text-[#0F172A] font-black text-sm">
                      {courierInitials}
                    </div>
                    <div>
                      <p className="text-xs font-black uppercase tracking-tight text-white">{trackingData.courier.name || "Votre livreur"}</p>
                      <p className="text-[9px] text-[#D4AF37] font-bold uppercase tracking-widest">Livreur Certifié Gold</p>
                      <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">Arrivée ~ {etaLabel}</p>
                    </div>
                  </div>
                  {trackingData.courier.phone && (
                    <a
                      href={`tel:${trackingData.courier.phone}`}
                      className="bg-[#003594] p-3 rounded-xl shadow-lg shadow-[#003594]/40 active:scale-90 transition-all text-[#D4AF37] hover:bg-[#003380]"
                      data-testid="tracking-call-driver"
                    >
                      <Phone size={18} />
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Devis Partagé WhatsApp — visible tant que la course est en cours */}
          {trackingData?.status && !['delivered', 'cancelled'].includes(trackingData.status) && (
            <div className="flex justify-end">
              <ShareOrderButton order={trackingData} />
            </div>
          )}

          {/* QR Verification Banner — affiché pendant le transport */}
          {['assigned', 'picked_up', 'in_transit'].includes(trackingData.status) && (
            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-5 rounded-[2rem] flex items-center gap-4" data-testid="qr-verification-banner">
              <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="text-[#D4AF37]" size={22} />
              </div>
              <div>
                <p className="text-[10px] font-black text-[#D4AF37] uppercase tracking-widest">Vérification QR Code</p>
                <p className="text-[10px] text-[#D4AF37]/80 font-medium leading-relaxed mt-1">
                  Scannez le code du livreur à l'arrivée pour valider la réception et libérer le paiement.
                </p>
              </div>
            </div>
          )}

          {/* Live Tracking Map */}
          <Card className="overflow-hidden shadow-2xl bg-[#1E293B] border border-white/5 rounded-[2.5rem]">
            <CardHeader className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] border-b border-white/5">
              <CardTitle className="flex items-center gap-2 text-white text-sm uppercase tracking-widest font-black">
                <Navigation className="w-5 h-5 text-[#D4AF37]" />
                {showLiveTracking ? 'Suivi GPS temps réel' : 'Carte du trajet'}
                {showLiveTracking && (
                  <span className="ml-auto text-[10px] bg-[#D4AF37] text-[#0F172A] px-2 py-1 rounded-full font-black tracking-widest">
                    🔴 LIVE
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <LiveTrackingMap
                orderId={orderId}
                pickupLocation={trackingData.pickup}
                deliveryLocation={trackingData.delivery}
                courierLocation={
                  liveCourierPos ||
                  (trackingData.courier ? [trackingData.courier.lat, trackingData.courier.lng] : null)
                }
                courierName={trackingData.courier?.name}
                orderStatus={trackingData.status}
                height="450px"
              />
            </CardContent>
          </Card>

          {/* Courier Info - visible uniquement pendant la livraison active */}
          {trackingData.courier && ['assigned', 'picked_up', 'in_transit'].includes(trackingData.status) && (() => {
            const rawPhone = (trackingData.courier.phone || '').toString().replace(/\D/g, '');
            // Format Sénégal par défaut si numéro local (9 chiffres) sans indicatif
            const intlPhone = rawPhone.length === 9 ? `221${rawPhone}` : rawPhone;
            const whatsappUrl = `https://wa.me/${intlPhone}?text=${encodeURIComponent(`Bonjour, je suis le client de la commande #${trackingData.order_id?.substring(0, 8)}`)}`;
            const telUrl = `tel:+${intlPhone}`;
            const displayPhone = rawPhone.length === 9
              ? `+221 ${rawPhone.slice(0, 2)} ${rawPhone.slice(2, 5)} ${rawPhone.slice(5, 7)} ${rawPhone.slice(7)}`
              : (trackingData.courier.phone || '—');
            return (
            <Card className="bg-[#0F172A] border-2 border-[#D4AF37]/40 shadow-2xl shadow-black/40 rounded-[2rem]" data-testid="courier-info-card">
              <CardHeader className="bg-gradient-to-r from-[#003594] to-[#003380] rounded-t-[2rem]">
                <CardTitle className="text-white flex items-center gap-2 text-base font-black uppercase tracking-widest">
                  <Truck className="w-5 h-5 text-[#D4AF37]" />
                  Votre Livreur
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                  <div className="flex items-center gap-4">
                    {trackingData.courier.avatar_url ? (
                      <img
                        src={trackingData.courier.avatar_url}
                        alt={trackingData.courier.name}
                        className="w-20 h-20 rounded-2xl object-cover ring-2 ring-[#D4AF37]/60 shadow-lg"
                        data-testid="courier-avatar"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-2xl flex items-center justify-center shadow-lg">
                        <Truck className="w-10 h-10 text-[#0F172A]" />
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] text-[#D4AF37] font-black uppercase tracking-[0.25em] mb-0.5">Pris en charge par</p>
                      <p className="text-xl md:text-2xl font-black text-white leading-tight" data-testid="courier-name">
                        {trackingData.courier.name}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-slate-300 mt-1.5 font-bold">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="text-white" data-testid="courier-rating">{trackingData.courier.rating?.toFixed(1) || '5.0'}</span>
                        <span className="text-slate-400">·</span>
                        <span>{trackingData.courier.total_deliveries || 0} livraisons</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-xs text-slate-200 font-bold uppercase tracking-widest">
                          {trackingData.courier.vehicle_type || 'Moto'}
                        </span>
                        {trackingData.courier.vehicle_plate && (
                          <span className="inline-flex items-center gap-1 bg-white text-slate-900 text-xs font-black tracking-widest px-3 py-1 rounded-md shadow-sm" data-testid="courier-plate">
                            {trackingData.courier.vehicle_plate}
                          </span>
                        )}
                      </div>
                      {trackingData.courier.phone && (
                        <p className="text-base font-black text-white mt-3 flex items-center gap-2" data-testid="courier-phone">
                          <Phone className="w-4 h-4 text-[#D4AF37]" />
                          {displayPhone}
                        </p>
                      )}
                    </div>
                  </div>
                  {trackingData.courier.phone && (
                    <div className="flex gap-2 flex-wrap">
                      <a
                        href={telUrl}
                        className="flex items-center gap-2 bg-[#10B981] hover:bg-[#059669] text-white px-6 py-3.5 rounded-2xl text-sm font-black uppercase tracking-widest shadow-lg shadow-emerald-500/30 transition-colors"
                        data-testid="call-courier-btn"
                      >
                        <Phone className="w-5 h-5" />
                        Appeler
                      </a>
                      <a
                        href={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white px-6 py-3.5 rounded-2xl text-sm font-black uppercase tracking-widest shadow-lg shadow-emerald-500/30 transition-colors"
                        data-testid="whatsapp-courier-btn"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        WhatsApp
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })()}

          {/* Info livreur masquée après livraison (rappel client) */}
          {trackingData.courier && trackingData.status === 'delivered' && (
            <Card className="bg-gray-50 border-gray-200" data-testid="courier-hidden-card">
              <CardContent className="pt-5 pb-5">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Les coordonnées du livreur sont masquées après la livraison pour votre sécurité et la sienne.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Bloc Signalement (dispute) - visible uniquement pour le client propriétaire */}
          {user.role === 'client' && trackingData.status !== 'cancelled' && (() => {
            if (existingDispute) {
              const statusColor = existingDispute.status === 'resolved'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800';
              return (
                <Card className={`border-2 ${statusColor}`} data-testid="dispute-status-card">
                  <CardContent className="pt-5 pb-5">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-semibold mb-1">
                          {existingDispute.status === 'resolved'
                            ? 'Signalement résolu'
                            : 'Signalement en cours de traitement'}
                        </p>
                        <p className="text-sm">
                          Motif : <span className="font-medium">{existingDispute.reason_label}</span>
                        </p>
                        {existingDispute.comment && (
                          <p className="text-sm mt-1 italic">« {existingDispute.comment} »</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            }
            return (
              <Card className="bg-[#0F172A] border-2 border-white/10 rounded-2xl shadow-xl" data-testid="dispute-card">
                <CardContent className="pt-5 pb-5">
                  {!showDispute ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-red-500/15 text-red-400 flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-base font-black text-white">Un problème avec cette commande ?</p>
                          <p className="text-sm text-slate-300 font-medium">
                            Colis non reçu, endommagé, en retard… signalez-le à notre équipe.
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => setShowDispute(true)}
                        className="bg-red-500 hover:bg-red-600 text-white px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-red-500/30"
                        data-testid="open-dispute-btn"
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Signaler
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                        <h3 className="font-black text-white uppercase tracking-widest text-sm">Signaler un problème</h3>
                      </div>
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-[#D4AF37] block mb-2">Motif</label>
                        <select
                          value={disputeReason}
                          onChange={(e) => setDisputeReason(e.target.value)}
                          className="w-full h-11 rounded-xl border border-white/10 px-3 bg-[#1E293B] text-white text-sm focus:ring-2 focus:ring-red-400 focus:border-transparent"
                          data-testid="dispute-reason-select"
                        >
                          <option value="">Choisissez un motif…</option>
                          {disputeReasons.map((r) => (
                            <option key={r.key} value={r.key}>{r.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-[#D4AF37] block mb-2">
                          Description (optionnel)
                        </label>
                        <textarea
                          value={disputeComment}
                          onChange={(e) => setDisputeComment(e.target.value)}
                          placeholder="Décrivez le problème en détail…"
                          className="w-full h-24 rounded-xl border border-white/10 bg-[#1E293B] text-white px-4 py-3 text-sm resize-none focus:ring-2 focus:ring-red-400 focus:border-transparent placeholder:text-slate-500"
                          data-testid="dispute-comment"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button
                          onClick={submitDispute}
                          disabled={!disputeReason || submittingDispute}
                          className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full font-black text-xs uppercase tracking-widest"
                          data-testid="submit-dispute-btn"
                        >
                          {submittingDispute ? "Envoi..." : "Envoyer le signalement"}
                        </Button>
                        <Button
                          onClick={() => { setShowDispute(false); setDisputeReason(""); setDisputeComment(""); }}
                          variant="outline"
                          className="rounded-full"
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Rating Section - appears after delivery — High Contrast */}
          {trackingData.status === 'delivered' && trackingData.courier && !hasRated && (
            <CourierRatingCard
              courierName={trackingData.courier.name}
              showRating={showRating}
              ratingScore={ratingScore}
              ratingComment={ratingComment}
              submittingRating={submittingRating}
              onOpenRating={() => setShowRating(true)}
              onScoreChange={setRatingScore}
              onCommentChange={setRatingComment}
              onSubmit={submitRating}
              onCancel={() => { setShowRating(false); setRatingScore(0); setRatingComment(""); }}
            />
          )}

          {/* Already rated message — High Contrast */}
          {trackingData.status === 'delivered' && hasRated && (
            <Card className="bg-[#0F172A] border-2 border-emerald-500/50 rounded-2xl shadow-lg">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-3 text-emerald-300">
                  <Star className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
                  <span className="text-base font-black text-white">Vous avez noté cette livraison. Merci !</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Addresses — High Contrast Cargo Style */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="addresses-block">
            <Card className="bg-[#0F172A] border-2 border-[#003594] shadow-xl shadow-blue-900/20 rounded-2xl">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#003594] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-900/40">
                    <span className="text-2xl">📦</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-[#D4AF37] font-black uppercase tracking-[0.3em] mb-1.5" data-testid="pickup-label">Départ</p>
                    <p className="text-base md:text-lg font-black text-white leading-snug break-words" data-testid="pickup-address-display">
                      {trackingData.pickup?.address || '—'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#0F172A] border-2 border-[#10B981] shadow-xl shadow-emerald-900/20 rounded-2xl">
              <CardContent className="pt-6 pb-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#10B981] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-900/40">
                    <span className="text-2xl">🏠</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-[#D4AF37] font-black uppercase tracking-[0.3em] mb-1.5" data-testid="delivery-label">Arrivée</p>
                    <p className="text-base md:text-lg font-black text-white leading-snug break-words" data-testid="delivery-address-display">
                      {trackingData.delivery?.address || '—'}
                    </p>
                    {trackingData.recipient_name && (
                      <p className="text-xs text-emerald-300 mt-2 font-bold uppercase tracking-widest">
                        Pour : <span className="text-white">{trackingData.recipient_name}</span>
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Timeline — High Contrast */}
          {trackingData.timestamps && (
            <Card className="bg-[#0F172A] border-2 border-white/10 rounded-2xl shadow-xl" data-testid="history-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white text-base font-black uppercase tracking-widest">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                  Historique
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {trackingData.timestamps.created_at && (
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-[#D4AF37] rounded-full ring-4 ring-[#D4AF37]/20" />
                      <div>
                        <p className="text-base font-black text-white">Commande créée</p>
                        <p className="text-sm text-slate-300 font-medium">
                          {new Date(trackingData.timestamps.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  )}
                  {trackingData.timestamps.prepared_at && (
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-cyan-400 rounded-full ring-4 ring-cyan-400/20" />
                      <div>
                        <p className="text-base font-black text-white">Commande prête</p>
                        <p className="text-sm text-slate-300 font-medium">
                          {new Date(trackingData.timestamps.prepared_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  )}
                  {trackingData.timestamps.picked_up_at && (
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-orange-400 rounded-full ring-4 ring-orange-400/20" />
                      <div>
                        <p className="text-base font-black text-white">Colis récupéré</p>
                        <p className="text-sm text-slate-300 font-medium">
                          {new Date(trackingData.timestamps.picked_up_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  )}
                  {trackingData.timestamps.delivered_at && (
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-emerald-400 rounded-full ring-4 ring-emerald-400/20" />
                      <div>
                        <p className="text-base font-black text-white">Livré ✓</p>
                        <p className="text-sm text-slate-300 font-medium">
                          {new Date(trackingData.timestamps.delivered_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
